const API_BASE = '/api/proxy';

// Thrown by fetchJson on non-2xx responses. Carries the raw backend error payload (e.g.
// ownedBundleIds on a 409 already-owned conflict) so callers can build a friendly message
// instead of dumping the raw error string.
export class PaymentApiError extends Error {
  status: number;
  /** Machine-readable code from the API envelope (e.g. EMAIL_NOT_VERIFIED).
   *  Branch on this rather than the message, which is display copy. */
  code?: string;
  /** Set on a PENDING_ORDER_EXISTS 409 — the order the buyer should resume. */
  pendingOrderId?: string;
  ownedModuleIds?: string[];
  ownedCourseIds?: string[];
  ownedBundleIds?: string[];
  /** Set on a 429 from POST /payment/orders/:id/refresh (1 call per 12s per user+order). */
  retryAfterSeconds?: number;
  /** Set on a CHECKOUT_IN_PROGRESS 409 — how long to wait before retrying. */
  retryAfterMs?: number;

  constructor(message: string, status: number, payload?: Record<string, unknown>) {
    super(message);
    this.name = 'PaymentApiError';
    this.status = status;
    this.code = payload?.code as string | undefined;
    this.pendingOrderId = payload?.pendingOrderId as string | undefined;
    this.ownedModuleIds = payload?.ownedModuleIds as string[] | undefined;
    this.ownedCourseIds = payload?.ownedCourseIds as string[] | undefined;
    this.ownedBundleIds = payload?.ownedBundleIds as string[] | undefined;
    this.retryAfterSeconds = payload?.retryAfterSeconds as number | undefined;
    this.retryAfterMs = payload?.retryAfterMs as number | undefined;
  }
}

// No Authorization header to attach anymore — /api/proxy forwards the httpOnly aks_session
// cookie automatically (same-origin fetch sends it by default). See
// docs/superpowers/specs/2026-07-18-session-cookie-auth-design.md §4.2.
async function fetchJson<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    ...options,
  });

  if (!response.ok) {
    // Fallback must be a shape whose .error is falsy, or the `HTTP ${status}` branch can
    // never fire and real status codes get masked as "Unknown error" (bit us on 2026-07-22).
    const error = await response.json().catch(() => ({}) as { error?: string });
    throw new PaymentApiError(error.error || `HTTP ${response.status}`, response.status, error);
  }

  return response.json();
}

export interface PaymentMethodResponse {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
  gateway: string;
  iconUrl: string | null;
  sortOrder: number;
  minAmount: number | null;
  maxAmount: number | null;
  feeFlat: number;
  feePercent: number;
  isActive: boolean;
}

export type PaymentInstructions =
  // receiverName is the bank-app-visible account name for the VA (e.g. "PTAKSAIN
  // Aksademia") — optional because older/cached instructions payloads predate it.
  | { type: 'VA'; bank: string; vaNumber: string; receiverName?: string }
  | { type: 'QRIS'; qrString: string; qrImageUrl?: string }
  | { type: 'EWALLET'; provider: string; deeplinkUrl?: string; qrImageUrl?: string };

export interface CreateOrderResponse {
  orderId: string;
  status: string;
  paymentUrl: string | null;
  expiresAt: string | null;
  subtotalAmount: number;
  discountAmount: number;
  // Payment-method surcharge, already folded into totalAmount below — exposed
  // separately so a confirmation/receipt view can show the same "Biaya" row
  // the cart did. Null for a free order (no payment method involved).
  feeAmount?: number | null;
  totalAmount: number;
  currency: string;
  purchaseCount: number;
  // Payment processor for this order, shown in the VA destination block — see
  // PaymentInstructions.tsx's `gateway` prop / CartDrawer's ActiveOrder.gateway.
  // Not a constant: with a per-account gateway override, or after the Midtrans
  // cutover, it is no longer always the same processor. The API has always sent
  // this field; it was simply missing from this projection, which is what made
  // the destination block hardcode "DOKU" here (dashboard's copy of this file
  // already had it).
  gateway?: string | null;
  paymentInstructions?: PaymentInstructions | null;
  // Server-truth bank/gateway identity for the VA destination block — hydrated from the
  // same Order.paymentMethod relation getOrder returns, available immediately after
  // checkout starts rather than only after a later getOrder poll. See OrderPaymentMethod's
  // `code` comment: this endpoint's projection omits it.
  paymentMethod?: OrderPaymentMethod | null;
}

export interface ValidateDiscountResponse {
  valid: boolean;
  code?: string;
  label?: string;
  type?: 'PERCENTAGE' | 'FIXED_AMOUNT';
  value?: number;
  discountAmount?: number;
  reason?: string;
}

export interface OwnedItemsResponse {
  moduleIds: string[];
  courseIds: string[];
  bundleIds: string[];
}

export interface OrderPaymentMethod {
  id: string;
  name: string;
  // Optional: createOrder's paymentMethod (below) is a slimmer projection than
  // getOrder/refresh's (which hydrate the full PaymentMethod relation) and omits
  // this field. Nothing reads .code off an order's paymentMethod today — it's
  // only used to match the picker's selectedMethod against PaymentMethodResponse.
  code?: string;
  iconUrl: string | null;
}

// Mirrors apps/dashboard/src/lib/api/payments.ts's OrderResponse — kept as a separate
// copy rather than a shared import, consistent with the pre-existing per-app
// duplication of CartContext/cart-storage/payments.ts across landing-page and
// dashboard. @test-prep/ui exists (dashboard already depends on it) but its Drawer
// component has no footer slot and isn't a fit for this file's multi-state checkout
// UI — see PR description for the full "why not unify" reasoning.
export interface OrderResponse {
  id: string;
  orderNumber: string | null;
  userId: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED' | 'PARTIALLY_REFUNDED' | 'EXPIRED';
  // True when this FAILED order carries a USER_CANCELLED OrderEvent — i.e. the
  // buyer deliberately cancelled via POST /orders/:id/cancel, not a gateway-side
  // deny/failure. Only meaningful when status is FAILED. See
  // deriveCancelledByUser (apps/api) for the derivation.
  cancelledByUser?: boolean;
  gateway: string;
  gatewayPaymentUrl: string | null;
  paymentMethod: OrderPaymentMethod | null;
  totalAmount: number;
  currency: string;
  paidAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  purchases: Array<{
    id: string;
    testModuleId: string | null;
    courseId: string | null;
    bundleId: string | null;
    testModule: { id: string; title: string; slug: string | null } | null;
    course: { id: string; title: string; slug: string | null } | null;
    bundle: { id: string; title: string; slug: string | null } | null;
    amount: number;
    status: string;
    productTitle: string | null;
    productKind: 'module' | 'course' | 'bundle' | null;
  }>;
  paymentInstructions?: PaymentInstructions | null;
}

// Response of POST /payment/orders/:id/refresh — a force-check against DOKU for this one
// order, bypassing the 60s reconciliation sweep's 2-minute-old floor. Same shape as
// OrderResponse (the order as it stands after the check) plus `changed`, which is false
// in the common case: the order was already terminal, had no gateway invoice, or is
// still genuinely PENDING after asking the gateway. `changed: false` is not an error.
export interface RefreshOrderResponse extends OrderResponse {
  changed: boolean;
}

// Response of POST /payment/orders/:id/cancel. `cancelled: false` is not an error —
// it means the live gateway check found the order already settled (e.g. PAID) and
// fulfilled it through the normal pipeline instead of cancelling; `order` reflects
// that real outcome either way. A CANCEL_FAILED_TRY_AGAIN / CANCEL_UNSUPPORTED /
// ORDER_NOT_CANCELLABLE outcome throws PaymentApiError instead (see fetchJson) —
// the order is guaranteed to still be PENDING in that case.
export interface CancelOrderResponse {
  cancelled: boolean;
  order: OrderResponse;
}

// A PENDING order whose deadline has already passed is effectively EXPIRED — mirrors
// the same helper in dashboard's payments.ts. Single source of truth per app; keep the
// two copies in sync if this logic ever changes.
export function isPaymentClientExpired(status: string, expiresAt: string | null): boolean {
  return status === 'PENDING' && expiresAt != null && new Date(expiresAt) < new Date();
}

export const paymentApi = {
  // `amount`, when passed, filters out methods whose minAmount/maxAmount band doesn't
  // admit this order — UX only, the server enforces the same rule again at charge time.
  getPaymentMethods: (amount?: number): Promise<PaymentMethodResponse[]> =>
    fetchJson(`/payment/methods${amount !== undefined ? `?amount=${amount}` : ''}`),

  createOrder: (params: {
    items: Array<{ testModuleId?: string; courseId?: string; bundleId?: string; eventId?: string }>;
    idempotencyKey: string;
    paymentMethodCode?: string;
    discountCode?: string;
    successRedirectUrl?: string;
    failureRedirectUrl?: string;
  }): Promise<CreateOrderResponse> =>
    fetchJson('/payment/orders', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  validateDiscountCode: (params: {
    code: string;
    items: Array<{ testModuleId?: string; courseId?: string; bundleId?: string; eventId?: string }>;
  }): Promise<ValidateDiscountResponse> =>
    fetchJson('/payment/discount-codes/validate', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // Used to recover an in-progress order (e.g. after a page reload) — see
  // CartDrawer's `order` deep-link param handling.
  getOrder: (orderId: string): Promise<OrderResponse> =>
    fetchJson(`/payment/orders/${orderId}`),

  getOwnedItems: (): Promise<OwnedItemsResponse> =>
    fetchJson('/payment/owned'),

  // Manual "Cek Status" button — force-checks the gateway for just this order instead
  // of waiting on the 60s sweep (which only considers orders >= 2 minutes old). Rate
  // limited server-side to 1 call per 12s per user+order (429 with retryAfterSeconds).
  refreshOrderStatus: (orderId: string): Promise<RefreshOrderResponse> =>
    fetchJson(`/payment/orders/${orderId}/refresh`, { method: 'POST' }),

  // "Batalkan Pesanan" button — only ever shown for a still-PENDING order whose
  // gateway supports a safe cancel (see CartDrawer's activeOrder.gateway check).
  // Rate limited server-side to 1 call per 12s per user+order (429 with
  // retryAfterSeconds), same shape as refreshOrderStatus.
  cancelOrder: (orderId: string): Promise<CancelOrderResponse> =>
    fetchJson(`/payment/orders/${orderId}/cancel`, { method: 'POST' }),
};
